import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import * as Yup from "yup";
import PageTitle from "../../components/PageTitle/PageTitle";
import Widget from "../../components/Widget/Widget";
import { showAppConfirm } from "../../context/AlertContext";
import OriginMasterService, {
  normalizeDomainName,
} from "./Locality/Service/originMasterService";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#0f3b62ad",
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  tableOverflow: {
    overflowX: "auto",
  },
  statusChip: {
    padding: "4px 8px",
    borderRadius: 4,
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "capitalize",
  },
  dialogField: {
    width: "100%",
    marginTop: 8,
    marginBottom: 4,
  },
  featureRow: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    marginTop: 8,
  },
  featureBox: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 10,
    marginTop: 8,
    background: "#f8fafc",
  },
}));

const ORIGIN_OPTIONS = ["India", "US"];

const emptyFeature = () => ({
  name: "",
  description: "",
  enabled: true,
});

const emptyForm = {
  name: "",
  origin: "",
  domainName: "",
  features: [emptyFeature()],
  status: true,
};

const normalizeFeaturesForForm = (features) => {
  if (!Array.isArray(features) || features.length === 0) return [emptyFeature()];
  return features.map((f) => ({
    name: f?.name || "",
    description: f?.description || "",
    enabled: f?.enabled !== false,
  }));
};

export default function OriginMaster() {
  const classes = useStyles();
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isSuperAdmin = String(userDetails?.role || "").toUpperCase() === "SUPERADMIN";

  const [rows, setRows] = useState([]);
  const [pg, setPg] = useState(0);
  const [rpg, setRpg] = useState(10);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [current, setCurrent] = useState(emptyForm);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Name is required"),
    origin: Yup.string()
      .required("Origin is required")
      .oneOf(ORIGIN_OPTIONS, "Select India or US"),
    domainName: Yup.string()
      .trim()
      .required("Domain name is required")
      .test(
        "hostname",
        "Use a host or host:port (e.g. localhost or localhost:3000), without http://",
        (value) => Boolean(normalizeDomainName(value)),
      ),
    features: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().trim().required("Feature name is required"),
          description: Yup.string().trim().nullable(),
          enabled: Yup.boolean(),
        }),
      )
      .min(1, "Add at least one feature"),
    status: Yup.boolean().required("Status is required"),
  });

  const formik = useFormik({
    initialValues: current,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!isSuperAdmin) {
        alert("Only SUPERADMIN can manage Origin Master.");
        return;
      }
      setSaving(true);
      try {
        const body = {
          name: String(values.name || "").trim(),
          origin: String(values.origin || "").trim(),
          domainName: normalizeDomainName(values.domainName),
          features: (values.features || [])
            .map((f) => ({
              name: String(f.name || "").trim(),
              description: String(f.description || "").trim(),
              enabled: f.enabled !== false,
            }))
            .filter((f) => f.name),
          status: Boolean(values.status),
        };
        if (!body.domainName) {
          alert("Use a host or host:port (e.g. localhost or localhost:3000), without http://");
          setSaving(false);
          return;
        }
        if (isEditMode && current._id) {
          await OriginMasterService.updateOriginMaster(current._id, body);
          alert("Origin Master updated successfully!");
        } else {
          await OriginMasterService.createOriginMaster(body);
          alert("Origin Master added successfully!");
        }
        handleClose();
        getList();
      } catch (err) {
        alert(err?.response?.data?.message || err.message || "Save failed");
      } finally {
        setSaving(false);
      }
    },
  });

  const getList = () => {
    const body = {};
    if (statusFilter === "active") body.status = true;
    if (statusFilter === "inactive") body.status = false;
    OriginMasterService.findOriginMasterList(body)
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch((err) => {
        console.error(err);
        alert(err?.response?.data?.message || err.message || "Failed to load list");
      });
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleOpen = () => {
    setIsEditMode(false);
    setCurrent(emptyForm);
    formik.resetForm({ values: emptyForm });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEditMode(false);
    setCurrent(emptyForm);
  };

  const editRow = (row) => {
    const next = {
      _id: row._id,
      name: row.name || "",
      origin: row.origin || "",
      domainName: row.domainName || "",
      features: normalizeFeaturesForForm(row.features),
      status: row.status !== false,
    };
    setIsEditMode(true);
    setCurrent(next);
    formik.resetForm({ values: next });
    setOpen(true);
  };

  const deleteRow = async (row) => {
    if (!isSuperAdmin) {
      alert("Only SUPERADMIN can delete Origin Master.");
      return;
    }
    const confirmed = await showAppConfirm(
      `Delete "${row.name}"?`,
      { title: "Delete Origin Master", confirmLabel: "Delete" },
    );
    if (!confirmed) return;
    try {
      await OriginMasterService.deleteOriginMaster(row._id);
      alert("Origin Master deleted successfully!");
      getList();
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Delete failed");
    }
  };

  const addFeatureRow = () => {
    formik.setFieldValue("features", [...(formik.values.features || []), emptyFeature()]);
  };

  const removeFeatureRow = (index) => {
    const next = [...(formik.values.features || [])];
    next.splice(index, 1);
    formik.setFieldValue("features", next.length ? next : [emptyFeature()]);
  };

  const featureCountLabel = (row) => {
    const list = Array.isArray(row.features) ? row.features : [];
    const enabled = list.filter((f) => f && f.enabled !== false).length;
    return `${enabled}/${list.length}`;
  };

  if (!isSuperAdmin) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          Access denied. Origin Master is available only for SUPERADMIN.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <PageTitle title="Origin Master" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box display="flex" justifyContent="flex-end" gridGap={10} flexWrap="wrap">
            <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPg(0);
                }}
              >
                <MenuItem value="all">All status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" color="primary" onClick={handleOpen}>
              Add Origin Master
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget title="Origin Master List" upperTitle noBodyPadding bodyClass={classes.tableOverflow}>
            <TableContainer component={Paper} style={{ maxHeight: 520 }}>
              <Table stickyHeader className="mb-0">
                <TableHead>
                  <TableRow>
                    {["Name", "Origin", "Domain Name", "Features", "Status", "Edit", "Delete"].map((h) => (
                      <StyledTableCell key={h}>{h}</StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No records found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.slice(pg * rpg, pg * rpg + rpg).map((row) => (
                      <TableRow key={row._id} hover>
                        <TableCell>{row.name || "—"}</TableCell>
                        <TableCell>{row.origin || "—"}</TableCell>
                        <TableCell>{row.domainName || "—"}</TableCell>
                        <TableCell>{featureCountLabel(row)}</TableCell>
                        <TableCell>
                          <span
                            className={classes.statusChip}
                            style={{
                              backgroundColor: row.status !== false ? "#4caf50" : "#f44336",
                            }}
                          >
                            {row.status !== false ? "Active" : "Inactive"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <IconButton color="primary" onClick={() => editRow(row)} aria-label="edit">
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <IconButton color="secondary" onClick={() => deleteRow(row)} aria-label="delete">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 10, 25, 50]}
              count={rows.length}
              page={pg}
              onPageChange={(_, newPage) => setPg(newPage)}
              rowsPerPage={rpg}
              onRowsPerPageChange={(e) => {
                setRpg(parseInt(e.target.value, 10));
                setPg(0);
              }}
            />
          </Widget>
        </Grid>
      </Grid>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="origin-master-dialog"
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { width: 520, maxWidth: "95vw" } }}
      >
        <DialogTitle id="origin-master-dialog" style={{ paddingBottom: 0 }}>
          {isEditMode ? "Edit Origin Master" : "Add Origin Master"}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent style={{ paddingTop: 8, paddingBottom: 8 }}>
            <TextField
              className={classes.dialogField}
              fullWidth
              variant="outlined"
              size="small"
              margin="dense"
              id="name"
              name="name"
              label="Name *"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              className={classes.dialogField}
              select
              fullWidth
              variant="outlined"
              size="small"
              margin="dense"
              id="origin"
              name="origin"
              label="Origin *"
              value={formik.values.origin}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.origin && Boolean(formik.errors.origin)}
              helperText={formik.touched.origin && formik.errors.origin}
            >
              <MenuItem value="">
                <em>Select Origin</em>
              </MenuItem>
              {ORIGIN_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.dialogField}
              fullWidth
              variant="outlined"
              size="small"
              margin="dense"
              id="domainName"
              name="domainName"
              label="Domain Name *"
              placeholder="e.g. localhost:3000 or example.com"
              value={formik.values.domainName}
              onChange={formik.handleChange}
              onBlur={(e) => {
                formik.handleBlur(e);
                const normalized = normalizeDomainName(e.target.value);
                if (normalized) {
                  formik.setFieldValue("domainName", normalized);
                }
              }}
              error={formik.touched.domainName && Boolean(formik.errors.domainName)}
              helperText={
                (formik.touched.domainName && formik.errors.domainName) ||
                "Host or host:port is allowed (localhost:3000). Do not include http://"
              }
            />

            <Box className={classes.featureBox}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Features *
                </Typography>
                <Button
                  size="small"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={addFeatureRow}
                  style={{ textTransform: "none" }}
                >
                  Add feature
                </Button>
              </Box>
              {(formik.values.features || []).map((feature, index) => (
                <Box key={`feature-${index}`} className={classes.featureRow}>
                  <Box flex={1}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      margin="dense"
                      label="Feature name *"
                      name={`features.${index}.name`}
                      value={feature.name}
                      onChange={formik.handleChange}
                      error={
                        Boolean(formik.touched.features?.[index]?.name) &&
                        Boolean(formik.errors.features?.[index]?.name)
                      }
                      helperText={
                        formik.touched.features?.[index]?.name &&
                        formik.errors.features?.[index]?.name
                      }
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      margin="dense"
                      label="Feature description"
                      name={`features.${index}.description`}
                      value={feature.description}
                      onChange={formik.handleChange}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          size="small"
                          checked={feature.enabled !== false}
                          onChange={(e) =>
                            formik.setFieldValue(`features.${index}.enabled`, e.target.checked)
                          }
                        />
                      }
                      label={feature.enabled !== false ? "Enabled" : "Disabled"}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    color="secondary"
                    aria-label="remove feature"
                    onClick={() => removeFeatureRow(index)}
                    style={{ marginTop: 8 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {typeof formik.errors.features === "string" && (
                <Typography variant="caption" color="error">
                  {formik.errors.features}
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  size="small"
                  checked={Boolean(formik.values.status)}
                  onChange={(e) => formik.setFieldValue("status", e.target.checked)}
                  name="status"
                />
              }
              label={formik.values.status ? "Active" : "Inactive"}
              style={{ marginTop: 4, marginLeft: 0 }}
            />
          </DialogContent>
          <DialogActions style={{ padding: "8px 16px 12px" }}>
            <Button onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained" disabled={saving}>
              {saving ? "Saving..." : isEditMode ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
